/**
 * Learning Package Editor Page
 * Edits package metadata and rich description, and manages the three sortable content areas
 * (learning paths, quiz collections, training profiles) with drag-and-drop ordering.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Avatar, Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Spin, Tag, Tooltip, message } from 'antd';
import {
  ApartmentOutlined,
  ArrowLeftOutlined,
  BarChartOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExperimentOutlined,
  EyeOutlined,
  HolderOutlined,
  PlusOutlined,
  QrcodeOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  ShareAltOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PagesWrapper from '../../PagesWrapper';
import { getAllCountries } from '../../services/countriesService';
import RichTextEditor from '../../components/RichTextEditor';
import { AccessBanner, OwnershipManager } from '../../components/Ownership';
import { useOwnership } from '../../hooks/useOwnership';
import {
  addPackageItem,
  createLearningPackage,
  getAvailablePackageItems,
  getLearningPackage,
  publishLearningPackage,
  removePackageItem,
  reorderPackageItems,
  unpublishLearningPackage,
  updateLearningPackage,
  PackageItemTypes,
  PackageItemTypeSlugs,
} from '../../services/learningPackagesService';
import styles from './LearningPackages.module.css';

/**
 * One draggable row inside a package area.
 */
const SortableAreaItem = ({ item, canEdit, onRemove, t }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.itemId, disabled: !canEdit });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.areaItem} ${isDragging ? styles.areaItemDragging : ''}`}
    >
      <span className={styles.areaItemHandle} {...attributes} {...listeners}>
        <HolderOutlined />
      </span>
      <span className={styles.areaItemName}>{item.name}</span>
      {!item.isPublished && (
        <Tooltip title={t('learningPackages.unpublishedItem', 'Not published — hidden on the public page')}>
          <Tag color="orange" icon={<WarningOutlined />}>{t('learningPackages.draft', 'Draft')}</Tag>
        </Tooltip>
      )}
      {canEdit && (
        <Popconfirm
          title={t('learningPackages.removeItemConfirm', 'Remove this item from the package?')}
          onConfirm={() => onRemove(item.itemId)}
        >
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )}
    </div>
  );
};

/**
 * One sortable content area (paths, quizzes, or profiles).
 */
const PackageArea = ({ titleIcon, title, items, canEdit, onReorder, onRemove, onAdd, t }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex(i => i.itemId === active.id);
    const newIndex = items.findIndex(i => i.itemId === over.id);
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <Card
      className={styles.areaCard}
      title={(
        <div className={styles.areaHeader}>
          <span className={styles.areaTitle}>
            <span className={styles.areaIcon}>{titleIcon}</span>
            {title}
          </span>
          {canEdit && (
            <Button size="small" icon={<PlusOutlined />} onClick={onAdd}>
              {t('learningPackages.addItem', 'Add')}
            </Button>
          )}
        </div>
      )}
    >
      {items.length === 0 ? (
        <div className={styles.areaEmpty}>{t('learningPackages.emptyArea', 'No items in this area yet')}</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map(i => i.itemId)} strategy={verticalListSortingStrategy}>
            <div className={styles.areaList}>
              {items.map(item => (
                <SortableAreaItem key={item.itemId} item={item} canEdit={canEdit} onRemove={onRemove} t={t} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </Card>
  );
};

/**
 * Renders the learning package editor with details, rich description, and area management.
 * @returns {JSX.Element} Learning package editor page
 */
const LearningPackageEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const packageId = id ? parseInt(id, 10) : null;
  const isNew = !packageId;

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ownershipModalOpen, setOwnershipModalOpen] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCountriesLoading(true);
      try {
        const result = await getAllCountries({ suppressNotification: true });
        if (!cancelled) setCountries(result || []);
      } catch {
        if (!cancelled) message.error(t('learningPackages.countriesLoadError', 'Failed to load languages'));
      } finally {
        if (!cancelled) setCountriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addItemType, setAddItemType] = useState(PackageItemTypes.LearningPath);
  const [availableItems, setAvailableItems] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [adding, setAdding] = useState(false);

  const ownership = useOwnership('LearningPackage', packageId, {
    autoLoad: !!packageId,
    onShareSuccess: () => message.success(t('ownership.shareSuccess', 'Access shared successfully')),
    onRevokeSuccess: () => message.success(t('ownership.revokeSuccess', 'Access revoked successfully')),
  });

  const canEdit = isNew || ownership.canEdit;

  const loadPackage = useCallback(async () => {
    if (!packageId) return;
    setLoading(true);
    try {
      const result = await getLearningPackage(packageId);
      setPkg(result);
      form.setFieldsValue({
        name: result.name,
        countryId: result.countryId,
        descriptionRichContent: result.descriptionRichContent || '',
      });
    } catch (error) {
      message.error(t('learningPackages.fetchError', 'Failed to load learning packages'));
    } finally {
      setLoading(false);
    }
  }, [packageId, form, t]);

  useEffect(() => {
    loadPackage();
  }, [loadPackage]);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (isNew) {
        const created = await createLearningPackage(values, { successMessage: t('learningPackages.createSuccess', 'Learning package created') });
        navigate(`/learning-packages/${created.id}`, { replace: true });
      } else {
        await updateLearningPackage(packageId, values, { successMessage: t('learningPackages.saveSuccess', 'Learning package saved') });
        loadPackage();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    setSaving(true);
    try {
      if (pkg?.isPublished) {
        await unpublishLearningPackage(packageId, { successMessage: t('learningPackages.unpublishSuccess', 'Learning package unpublished') });
      } else {
        await publishLearningPackage(packageId, { successMessage: t('learningPackages.publishSuccess', 'Learning package published'), suppressNotification: true });
      }
      loadPackage();
    } catch (error) {
      // Publish validation failures (e.g. no published items) come back as 400 with a message.
      message.warning(error?.message || t('learningPackages.publishError', 'Failed to change publish state'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyPublicLink = async () => {
    const url = `${window.location.origin}/packages/${packageId}`;
    await navigator.clipboard.writeText(url);
    message.success(t('learningPackages.linkCopied', 'Public link copied'));
  };

  const handleDownloadQrCode = () => {
    const svg = document.getElementById('learning-package-editor-qr-code');
    if (!svg) {
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${pkg?.name || 'learning-package'}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };

    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  };

  const openAddModal = async (itemType) => {
    setAddItemType(itemType);
    setSelectedItemId(null);
    setAddModalOpen(true);
    setAvailableLoading(true);
    try {
      const result = await getAvailablePackageItems(packageId, PackageItemTypeSlugs[itemType]);
      setAvailableItems(result || []);
    } catch (error) {
      message.error(t('learningPackages.fetchError', 'Failed to load learning packages'));
    } finally {
      setAvailableLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!selectedItemId) return;
    setAdding(true);
    try {
      await addPackageItem(packageId, addItemType, selectedItemId, { successMessage: t('learningPackages.itemAdded', 'Item added') });
      setAddModalOpen(false);
      loadPackage();
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveItem = async (itemType, itemId) => {
    await removePackageItem(packageId, PackageItemTypeSlugs[itemType], itemId, { successMessage: t('learningPackages.itemRemoved', 'Item removed') });
    loadPackage();
  };

  const handleReorder = async (itemType, newItems, setLocal) => {
    setLocal(newItems);
    try {
      await reorderPackageItems(packageId, itemType, newItems.map(i => i.itemId), { suppressNotification: true });
    } catch (error) {
      message.error(t('learningPackages.reorderError', 'Failed to reorder items'));
      loadPackage();
    }
  };

  // Local copies of areas so drag-and-drop feels instant.
  const [paths, setPaths] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    setPaths(pkg?.paths || []);
    setQuizzes(pkg?.quizCollections || []);
    setProfiles(pkg?.trainingProfiles || []);
  }, [pkg]);

  const areaTypeLabels = useMemo(() => ({
    [PackageItemTypes.LearningPath]: t('learningPackages.areaPaths', 'Learning Paths'),
    [PackageItemTypes.QuizCollection]: t('learningPackages.areaQuizzes', 'Quiz Collections'),
    [PackageItemTypes.TrainingProfile]: t('learningPackages.areaProfiles', 'Training Profiles'),
  }), [t]);

  return (
    <PagesWrapper>
      <Spin spinning={loading}>
        <div className={styles.editorContainer}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/learning-packages')}>
                {t('common.back', 'Back')}
              </Button>
              <h1>
                {isNew
                  ? t('learningPackages.editorCreateTitle', 'Create Learning Package')
                  : (pkg?.name || t('learningPackages.editorEditTitle', 'Edit Learning Package'))}
              </h1>
              {pkg && (
                <Tag color={pkg.isPublished ? 'green' : 'default'}>
                  {pkg.isPublished ? t('learningPackages.published', 'Published') : t('learningPackages.draft', 'Draft')}
                </Tag>
              )}
              {pkg?.addedByName && (
                <Tooltip title={t('learningPackages.createdBy', 'Created by')}>
                  <Space size="small" className={styles.createdBy}>
                    <Avatar size="small" icon={<UserOutlined />} />
                    <span>{pkg.addedByName}</span>
                  </Space>
                </Tooltip>
              )}
            </div>
            <Space className={styles.headerActions}>
              {pkg && (
                <Tooltip title={t('learningPackages.explorePackage', 'Explore Package')}>
                  <Button icon={<EyeOutlined />} disabled={!pkg.isPublished} onClick={() => window.open(`/packages/${pkg.id}`, '_blank')}>
                    {t('learningPackages.explorePackage', 'Explore Package')}
                  </Button>
                </Tooltip>
              )}
              {pkg && (
                <Tooltip title={t('learningPackages.statistics', 'Statistics')}>
                  <Button icon={<BarChartOutlined />} onClick={() => navigate(`/learning-packages/${pkg.id}/statistics`)}>
                    {t('learningPackages.statistics', 'Statistics')}
                  </Button>
                </Tooltip>
              )}
              {pkg && (
                <Tooltip title={t('learningPackages.copyPublicLink', 'Copy public link')}>
                  <Button icon={<CopyOutlined />} disabled={!pkg.isPublished} onClick={handleCopyPublicLink}>
                    {t('learningPackages.copyLink', 'Copy Link')}
                  </Button>
                </Tooltip>
              )}
              {pkg && (
                <Tooltip title={t('learningPackages.qrCode', 'QR Code')}>
                  <Button icon={<QrcodeOutlined />} disabled={!pkg.isPublished} onClick={() => setQrModalVisible(true)}>
                    {t('learningPackages.qrCode', 'QR Code')}
                  </Button>
                </Tooltip>
              )}
              {!isNew && ownership.canManageOwnership && (
                <Tooltip title={t('learningPackages.manageOwnership', 'Manage Ownership')}>
                  <Button icon={<ShareAltOutlined />} onClick={() => setOwnershipModalOpen(true)}>
                    {t('learningPackages.share', 'Share')}
                  </Button>
                </Tooltip>
              )}
              {!isNew && canEdit && (
                <Button onClick={handleTogglePublish} loading={saving}>
                  {pkg?.isPublished ? t('learningPackages.unpublish', 'Unpublish') : t('learningPackages.publish', 'Publish')}
                </Button>
              )}
              {canEdit && (
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                  {t('common.save', 'Save')}
                </Button>
              )}
            </Space>
          </div>

          {ownership.ownershipInfo && (
            <AccessBanner
              accessLevel={ownership.ownershipInfo.accessLevel}
              ownerInfo={ownership.ownershipInfo.mainOwner}
              canEdit={ownership.ownershipInfo.canEdit}
              canDelete={ownership.ownershipInfo.canDelete}
            />
          )}

          {isNew && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('learningPackages.saveBeforeItems', 'Save the package before adding items')}
            />
          )}

          <Card title={t('learningPackages.detailsCard', 'Package Details')}>
            <Form form={form} layout="vertical" disabled={!canEdit}>
              <Form.Item
                name="name"
                label={t('learningPackages.columnName', 'Name')}
                rules={[{ required: true, message: t('learningPackages.nameRequired', 'Name is required') }]}
              >
                <Input maxLength={255} placeholder={t('learningPackages.namePlaceholder', 'Package name')} />
              </Form.Item>
              <Form.Item
                name="countryId"
                label={t('learningPackages.languageLabel', 'Language')}
                rules={[{ required: true, message: t('learningPackages.languageRequired', 'Language is required') }]}
              >
                <Select
                  showSearch
                  loading={countriesLoading}
                  placeholder={t('learningPackages.languagePlaceholder', 'Choose a language')}
                  options={countries.map((country) => ({ value: country.id, label: country.language || country.name }))}
                  optionFilterProp="label"
                  filterOption={(input, option) => (option?.label?.toLowerCase?.() || '').includes(input.toLowerCase())}
                />
              </Form.Item>
              <Form.Item
                name="descriptionRichContent"
                label={t('learningPackages.descriptionLabel', 'Description')}
                extra={t('learningPackages.descriptionHint', 'Explain what this package teaches and how learners should use it.')}
              >
                <RichTextEditor
                  parentType="LearningPackage"
                  parentId={packageId}
                  maxLength={5000}
                  disabled={!canEdit}
                />
              </Form.Item>
            </Form>
          </Card>

          {!isNew && (
            <div className={styles.areasGrid}>
              <PackageArea
                titleIcon={<ApartmentOutlined />}
                title={areaTypeLabels[PackageItemTypes.LearningPath]}
                items={paths}
                canEdit={canEdit}
                onReorder={(newItems) => handleReorder(PackageItemTypes.LearningPath, newItems, setPaths)}
                onRemove={(itemId) => handleRemoveItem(PackageItemTypes.LearningPath, itemId)}
                onAdd={() => openAddModal(PackageItemTypes.LearningPath)}
                t={t}
              />
              <PackageArea
                titleIcon={<QuestionCircleOutlined />}
                title={areaTypeLabels[PackageItemTypes.QuizCollection]}
                items={quizzes}
                canEdit={canEdit}
                onReorder={(newItems) => handleReorder(PackageItemTypes.QuizCollection, newItems, setQuizzes)}
                onRemove={(itemId) => handleRemoveItem(PackageItemTypes.QuizCollection, itemId)}
                onAdd={() => openAddModal(PackageItemTypes.QuizCollection)}
                t={t}
              />
              <PackageArea
                titleIcon={<ExperimentOutlined />}
                title={areaTypeLabels[PackageItemTypes.TrainingProfile]}
                items={profiles}
                canEdit={canEdit}
                onReorder={(newItems) => handleReorder(PackageItemTypes.TrainingProfile, newItems, setProfiles)}
                onRemove={(itemId) => handleRemoveItem(PackageItemTypes.TrainingProfile, itemId)}
                onAdd={() => openAddModal(PackageItemTypes.TrainingProfile)}
                t={t}
              />
            </div>
          )}
        </div>
      </Spin>

      <Modal
        title={`${t('learningPackages.addItemTitle', 'Add item')} — ${areaTypeLabels[addItemType]}`}
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onOk={handleAddItem}
        confirmLoading={adding}
        okText={t('learningPackages.addItem', 'Add')}
        okButtonProps={{ disabled: !selectedItemId }}
      >
        {!availableLoading && availableItems.length === 0 ? (
          <Alert type="info" showIcon message={t('learningPackages.noAvailableItems', 'No more items available to add')} />
        ) : (
          <Select
            showSearch
            loading={availableLoading}
            style={{ width: '100%' }}
            placeholder={t('learningPackages.selectItemPlaceholder', 'Select an item to add...')}
            value={selectedItemId}
            onChange={setSelectedItemId}
            optionFilterProp="label"
            options={availableItems.map(item => ({
              value: item.itemId,
              label: item.isPublished ? item.name : `${item.name} (${t('learningPackages.draft', 'Draft')})`,
            }))}
          />
        )}
      </Modal>

      {pkg && (
        <Modal
          title={t('learningPackages.qrCode', 'QR Code')}
          open={qrModalVisible}
          onCancel={() => setQrModalVisible(false)}
          centered
          footer={[
            <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadQrCode}>
              {t('learningPackages.downloadQrCode', 'Download QR Code')}
            </Button>,
            <Button key="close" onClick={() => setQrModalVisible(false)}>
              {t('common.close', 'Close')}
            </Button>,
          ]}
        >
          <div className={styles.qrCodeContainer}>
            <QRCodeSVG
              id="learning-package-editor-qr-code"
              value={`${window.location.origin}/packages/${pkg.id}`}
              size={256}
              level="H"
              includeMargin
            />
            <p className={styles.qrCodeCollectionName}>{pkg.name}</p>
            <p className={styles.qrCodeLink}>{`${window.location.origin}/packages/${pkg.id}`}</p>
          </div>
        </Modal>
      )}

      <OwnershipManager
        open={ownershipModalOpen}
        onClose={() => setOwnershipModalOpen(false)}
        ownershipInfo={ownership.ownershipInfo}
        canManageOwnership={ownership.canManageOwnership}
        onShare={ownership.shareWith}
        onRevoke={ownership.revoke}
        onGetAvailableUsers={ownership.getAvailableUsers}
        availableUsers={ownership.availableUsers}
        loading={ownership.loading || ownership.loadingAvailableUsers}
      />
    </PagesWrapper>
  );
};

export default LearningPackageEditor;
